/**
 * ExportUpsellModal (surface-limits slice) — the export take-away gate.
 * =====================================================================
 * Demo users explore the full catalogue freely (browse, filter, switch axes,
 * view). The single PRO lever is the *export*: attempting to save a plot / radar
 * chart / ranking as an image opens this upsell instead of downloading, and the
 * attempt is recorded as a conversion signal (see demoLimitService.blockDemoExport).
 *
 * Rendered once (in AppShell), driven by demoLimitStore.exportUpsellOpen. The
 * copy is export-framed and carries NO price — a single primary CTA "Talk to our
 * team" routes to /checkout with a per-surface lead_source, and a tertiary
 * "Keep exploring — it's free" closes it (never a dead-end).
 *
 * Structure/classes are shared with DailyLimitModal (daily-limit-* CSS) so the
 * modal is already styled, focus-trapped, and reduced-motion-safe.
 */
import { createPortal } from 'preact/compat'
import { useEffect, useRef } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { trackEvent } from '../../modules/dataService.js'
import {
  exportUpsellOpen,
  exportUpsellSurface,
  closeExportUpsell,
} from '../../state/demoLimitStore.js'

// Surface-aware value line — falls back to a generic line for unknown surfaces.
const SURFACE_COPY = {
  free_plot: {
    label: 'this plot',
    line: 'Export the Graph as a publication-ready image — and take the full measured catalogue with you into your own decks and reports.',
  },
  radar: {
    label: 'this radar chart',
    line: 'Export Technology Radar charts as branded images for your own market briefings and reviews.',
  },
  cell_selection: {
    label: 'this ranking',
    line: 'Export your cell-selection ranking as an image to share the shortlist with your team.',
  },
}

export default function ExportUpsellModal() {
  const open = exportUpsellOpen.value
  const surface = exportUpsellSurface.value
  const [, setLocation] = useLocation()
  const dialogRef = useRef(null)
  const primaryRef = useRef(null)
  const previouslyFocused = useRef(null)

  // Track the block once per open, focus the primary CTA, restore focus on close.
  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement
    trackEvent('Demo Export Blocked', { surface: surface || 'unknown' })
    const id = requestAnimationFrame(() => {
      if (primaryRef.current) primaryRef.current.focus()
    })
    return () => {
      cancelAnimationFrame(id)
      const prev = previouslyFocused.current
      if (prev && typeof prev.focus === 'function') prev.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  function close() {
    closeExportUpsell()
  }

  function onOverlayClick(e) {
    if (e.target === e.currentTarget) close()
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation()
      close()
      return
    }
    // Minimal focus-trap: keep Tab within the dialog.
    if (e.key === 'Tab') {
      const focusables = dialogRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusables || focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  function onPrimaryCta() {
    const surfaceKey = surface || 'unknown'
    trackEvent('Demo Export Upgrade Clicked', {
      lead_source: `demo_export_${surfaceKey}`,
      surface: surfaceKey,
    })
    // Carry the attribution to the /contact inquiry submit (same mechanism as
    // DailyLimitModal), so the backend tags the sales inquiry to this surface.
    try {
      sessionStorage.setItem('demo_lead_source', `demo_export_${surfaceKey}`)
    } catch (e) {
      /* sessionStorage unavailable (private mode) — attribution best-effort */
    }
    close()
    // Lightweight "Talk to our team" contact page (demo users have no price).
    setLocation('/contact')
  }

  const copy = SURFACE_COPY[surface] || {
    label: 'this chart',
    line: 'Export your work as publication-ready images — and take the full measured catalogue with you.',
  }

  return createPortal(
    <div
      className="daily-limit-overlay"
      onClick={onOverlayClick}
      onKeyDown={onKeyDown}
    >
      <div
        className="daily-limit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-upsell-title"
        ref={dialogRef}
      >
        <button
          className="daily-limit-modal__close"
          onClick={close}
          aria-label="Close"
          type="button"
        >
          ×
        </button>

        <div className="daily-limit-modal__body">
          <p className="daily-limit-modal__eyebrow">Take it with you</p>
          <h2 className="daily-limit-modal__title" id="export-upsell-title">
            Exports are a PRO feature.
          </h2>

          <p className="daily-limit-modal__reframe">
            {copy.line}{' '}
            <strong>Browsing, filtering, and deep-dives stay free.</strong>
          </p>

          {/* One value-framed primary CTA (no price shown) */}
          <button
            className="daily-limit-modal__cta"
            onClick={onPrimaryCta}
            ref={primaryRef}
            type="button"
          >
            Talk to our team
          </button>
          <p className="daily-limit-modal__risk-off">
            No commitment — just a conversation. Most teams hear back within one
            business day.
          </p>

          {/* Social proof (shared styling with the daily-limit modal) */}
          <div className="daily-limit-modal__proof">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l2.4 7.4H22l-6 4.4 2.3 7.2L12 16.6 5.7 21l2.3-7.2-6-4.4h7.6z" />
            </svg>
            Trusted by OEMs and tier-1 suppliers for measured cell data.
          </div>

          {/* Tertiary — no dead-end */}
          <div className="daily-limit-modal__secondary">
            <button
              className="daily-limit-modal__tertiary"
              onClick={close}
              type="button"
            >
              Keep exploring — it's free
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
